/**
 * v4Validate — Phase 5e-1.
 *
 * Pure analyzer for V4 canvas payloads. Returns a list of issues +
 * aggregate stats; no React, no side effects, no DOM access. Safe to
 * call from a useMemo on every payload change.
 *
 * Issue shape:
 *   {
 *     id:       string             // stable per-issue id for React keys
 *     severity: 'error'|'warning'|'info'
 *     code:     string             // machine code (e.g. 'MISSING_START')
 *     message:  string             // human-readable, present-tense
 *     nodeId?:  string             // node the issue points at (canvas id, not rfNode id)
 *     edgeId?:  string             // edge the issue points at
 *     suggest?: string             // optional one-line fix hint
 *   }
 *
 * Stats shape:
 *   { error: N, warning: N, info: N, total: N }
 *
 * Severity policy:
 *   error   → workflow won't save / won't run successfully (missing
 *             Start, missing End, cycle, required config field empty)
 *   warning → workflow runs but author should review (orphan node,
 *             unreachable, dead-end, dangling branch, self-loop)
 *   info    → opportunity / suggestion (e.g. node has no label)
 *
 * NOT exhaustive — first pass covers the common-case shape errors that
 * V3 lets through. Easy to extend per node type as the V4 surface grows.
 */

/* Sentinel types — these don't require Start-to-here paths. */
const ENTRY_TYPES = new Set(['manual_start', 'start']);
const END_TYPES = new Set(['end', 'terminate']);

// RC-112b — Same drift-validator infrastructure as v3Validate.js.
// NODE_TYPES imported so the assertion at the bottom of this file can
// guarantee LEGACY_REQUIRED_CONFIG field names track the schemas.
import { NODE_TYPES as NODE_TYPES_FOR_DRIFT } from '../../data/soarNodes';
import { branchDisplayName, branchIsWired } from '../../utils/canvas/branchIdentity';
import { validateFormTaskAssignment } from '../shared/formTaskAssignmentValidator';
import {
  LEGACY_REQUIRED_CONFIG,
  schemaFieldName,
  missingRequiredConfig,
} from '../../utils/canvas/requiredConfig';

/* Per-node-type required config fields. Empty string + undefined +
 * null + empty-object all count as "missing". For branch heads
 * (switch_case, parallel_fork, fork) we also validate that the
 * branches/cases array isn't empty AND each branch has a name.
 *
 * RC-112a — Audited 2026-06-09 against soarNodes.js configSchemas.
 * See v3Validate.js for the full divergence narrative. */

/**
 * Main entry. Accepts a V4 canvas payload `{ nodes, connections }`
 * and returns an analysis report.
 */
export function validateWorkflow(payload) {
  const nodes = Array.isArray(payload?.nodes) ? payload.nodes : [];
  const connections = Array.isArray(payload?.connections) ? payload.connections : [];
  const issues = [];

  // Empty graph short-circuit — caller's empty-state surface explains the
  // "no workflow loaded" case; validator stays quiet.
  if (nodes.length === 0) {
    return { issues: [], stats: emptyStats() };
  }

  // Maps used by multiple checks
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const outEdges = new Map(); // nodeId → connections[]
  const inEdges = new Map();
  /* Cycle-only adjacency: outEdges MINUS designed back-edges (`loop_back` = Do-While
   * iteration; `cognitive_return` = cognitive-loop tool return, AGENT_EXECUTION_MODEL §4/§7).
   * Counting them made CYCLE_DETECTED fire on every cognitive agent and every Do-While.
   * Reachability/orphan checks keep the FULL outEdges — only acyclicity is exempted; a real
   * cycle on structural edges still errors. Mirrors AUX_SOURCE_HANDLES / BACK_EDGE_PORTS. */
  const cycleOutEdges = new Map();
  const isBackEdge = (c) => c && (c.fromPort === 'loop_back' || c.fromPort === 'cognitive_return'
    || c.sourceHandle === 'loop_back' || c.sourceHandle === 'cognitive_return');
  connections.forEach((c) => {
    if (!c?.from || !c?.to) return;
    if (!outEdges.has(c.from)) outEdges.set(c.from, []);
    outEdges.get(c.from).push(c);
    if (!inEdges.has(c.to)) inEdges.set(c.to, []);
    inEdges.get(c.to).push(c);
    if (!isBackEdge(c)) {
      if (!cycleOutEdges.has(c.from)) cycleOutEdges.set(c.from, []);
      cycleOutEdges.get(c.from).push(c);
    }
  });

  // ── Check 1 — Sentinel presence ────────────────────────────────────
  const startNodes = nodes.filter((n) => ENTRY_TYPES.has(n.type));
  const endNodes = nodes.filter((n) => END_TYPES.has(n.type) && n.type !== 'terminate');
  // Note: `terminate` is a non-default exit, not a substitute for `end`.
  // We require at least one `end` OR one `terminate` to count as "has exit".
  const hasExit = nodes.some((n) => END_TYPES.has(n.type));

  if (startNodes.length === 0) {
    issues.push({
      id: 'missing-start',
      severity: 'error',
      code: 'MISSING_START',
      message: 'Workflow has no Start sentinel. Every workflow needs a single manual_start node.',
      suggest: 'Drag a Start node from the left palette.',
    });
  }
  if (startNodes.length > 1) {
    startNodes.forEach((n, idx) => {
      if (idx === 0) return; // first one is canonical
      issues.push({
        id: `multi-start-${n.id}`,
        severity: 'error',
        code: 'MULTIPLE_START',
        nodeId: n.id,
        message: `Multiple Start sentinels detected. A workflow can have only one entry point.`,
        suggest: 'Delete the extra Start node — workflows must have exactly one.',
      });
    });
  }
  if (!hasExit) {
    issues.push({
      id: 'missing-end',
      severity: 'error',
      code: 'MISSING_END',
      message: 'Workflow has no End or Terminate sentinel. The runtime cannot determine when the workflow has completed.',
      suggest: 'Drag an End node from the left palette.',
    });
  }

  // ── Check 2 — Duplicate node ids (defensive — useCanvas shouldn't allow this) ──
  const seenIds = new Map();
  nodes.forEach((n) => {
    if (seenIds.has(n.id)) {
      issues.push({
        id: `duplicate-id-${n.id}`,
        severity: 'error',
        code: 'DUPLICATE_NODE_ID',
        nodeId: n.id,
        message: `Two nodes share the id ${n.id}. This will cause undefined behavior at runtime.`,
        suggest: 'Delete one of the duplicates.',
      });
    } else {
      seenIds.set(n.id, n);
    }
  });

  // ── Check 3 — Self-loops ───────────────────────────────────────────
  connections.forEach((c) => {
    if (c.from && c.to && c.from === c.to) {
      issues.push({
        id: `self-loop-${c.id || `${c.from}-${c.to}`}`,
        severity: 'warning',
        code: 'SELF_LOOP',
        nodeId: c.from,
        edgeId: c.id,
        message: `Node has an edge to itself. Self-loops are rarely intentional outside of explicit retry constructs.`,
        suggest: 'Remove the self-edge or replace with a do_while head.',
      });
    }
  });

  // ── Check 4 — Orphan + reachability + dead-end ─────────────────────
  // An orphan: no incoming AND no outgoing edges, AND not a sentinel.
  // Unreachable: no path from any Start node (excluding the Start nodes themselves).
  // Dead-end: no path to any End/Terminate node (excluding sentinels).
  const reachable = bfs(startNodes.map((n) => n.id), outEdges);
  const exitNodeIds = nodes.filter((n) => END_TYPES.has(n.type)).map((n) => n.id);
  const productive = reverseBfs(exitNodeIds, inEdges);

  nodes.forEach((n) => {
    const isSentinel = ENTRY_TYPES.has(n.type) || END_TYPES.has(n.type);
    const hasIn = (inEdges.get(n.id) || []).length > 0;
    const hasOut = (outEdges.get(n.id) || []).length > 0;

    if (!isSentinel && !hasIn && !hasOut) {
      issues.push({
        id: `orphan-${n.id}`,
        severity: 'warning',
        code: 'ORPHAN_NODE',
        nodeId: n.id,
        message: `Node "${n.label || n.type}" has no connections — it will never execute.`,
        suggest: 'Wire it up or delete it.',
      });
      return; // skip the more specific checks for fully-orphan nodes
    }
    if (!isSentinel && !reachable.has(n.id)) {
      issues.push({
        id: `unreachable-${n.id}`,
        severity: 'warning',
        code: 'UNREACHABLE_FROM_START',
        nodeId: n.id,
        message: `Node "${n.label || n.type}" is not reachable from Start.`,
        suggest: 'Add an incoming edge from a node upstream of Start.',
      });
    }
    if (!isSentinel && !productive.has(n.id) && hasOut === false && hasIn === true) {
      issues.push({
        id: `dead-end-${n.id}`,
        severity: 'warning',
        code: 'DEAD_END_NODE',
        nodeId: n.id,
        message: `Node "${n.label || n.type}" has no path to End — execution will halt here without reaching a sentinel.`,
        suggest: 'Add an outgoing edge to End / Terminate or onto a downstream node.',
      });
    }
  });

  // ── Check 5 — Cycle detection (Kahn-style topological sort fails on cycles) ──
  const cycleNodes = findCycleNodes(nodes, cycleOutEdges);
  if (cycleNodes.size > 0) {
    // Group all cycle nodes under a single issue so the panel doesn't get noisy
    issues.push({
      id: 'cycle',
      severity: 'error',
      code: 'CYCLE_DETECTED',
      // Point the issue at the first node in the cycle for jump-to-node
      nodeId: Array.from(cycleNodes)[0],
      message: `Cycle detected involving ${cycleNodes.size} node(s): ${Array.from(cycleNodes)
        .map((id) => nodeById.get(id)?.label || nodeById.get(id)?.type || id)
        .slice(0, 4)
        .join(', ')}${cycleNodes.size > 4 ? ', …' : ''}.`,
      suggest: 'Workflows must be acyclic. Replace with a loop / do_while head if iteration was intended.',
    });
  }

  // ── Check 6 — Dangling branches on switch/fork heads ───────────────
  // A switch_case head whose config.cases defines branches but has no
  // outgoing edges from those branch ports. Same for parallel_fork.branches.
  //
  // RC-112d — see v3Validate.js for the full narrative. Field name was
  // `legs` here too; schema declares `branches`. Pre-RC-112d the check
  // silently did nothing for parallel_fork (legs always empty → early
  // return). Now reads branches with legs as legacy fallback.
  nodes.forEach((n) => {
    const cfg = n.config || {};
    const cases = Array.isArray(cfg.cases) ? cfg.cases : [];
    const forkBranches = Array.isArray(cfg.branches) ? cfg.branches
                       : Array.isArray(cfg.legs) ? cfg.legs
                       : [];
    const branches = n.type === 'switch_case' ? cases
                   : (n.type === 'parallel_fork' || n.type === 'fork') ? forkBranches
                   : [];
    if (branches.length === 0) return;
    const out = outEdges.get(n.id) || [];
    branches.forEach((b) => {
      // Shared with v3Validate and v2Lint — utils/canvas/branchIdentity.js.
      // This copy omitted `id` (the port the platform's fork generator emits)
      // AND matched case-sensitively, so it reported correctly-wired branches
      // as dangling on two independent counts.
      const branchName = branchDisplayName(b);
      if (!branchName) return;
      const hasEdge = branchIsWired(out, b);
      if (!hasEdge) {
        issues.push({
          id: `dangling-${n.id}-${branchName}`,
          severity: 'warning',
          code: 'DANGLING_BRANCH',
          nodeId: n.id,
          message: `Branch "${branchName}" on ${n.type} "${n.label || n.id}" has no downstream node — execution along this branch will halt.`,
          suggest: `Drop a node into the "${branchName}" branch container, or remove the branch from config.`,
        });
      }
    });
  });

  // ── Check 7 — Per-node-type required config completeness ───────────
  nodes.forEach((n) => {
    /* Shared with v3Validate — utils/canvas/requiredConfig.js.
     *
     * This copy walked its own table and did NOT understand the `a|b`
     * OR-form, so every `terminate` node was reported as missing a field
     * literally named "terminationStatus|status" no matter how it was
     * configured. One implementation cannot disagree with itself. */
    missingRequiredConfig(n).forEach(({ field, severity }) => {
      issues.push({
        id: `config-${n.id}-${field}`,
        severity,
        code: 'REQUIRED_CONFIG_MISSING',
        nodeId: n.id,
        message: `${humanizeType(n.type)} "${n.label || n.id}" is missing required config: ${field}.`,
        suggest: `Open the node in the Inspector and fill in ${field}.`,
      });
    });
  });

  // ── Check 7b — form_task assignment shape (RC-220) ─────────────────
  //
  // Shared with v3Validate.js — see shared/formTaskAssignmentValidator.js
  // for the full contract. Validates the polymorphic assignment block
  // (mode + assigneeType + per-type ref shape) against the 5-mode model
  // from RC-219 (USER/GROUP/ROLE + DYNAMIC/EXTERNAL_SUBJECT). Skips
  // form_tasks without an `assignment` block (legacy Approval Center path).
  nodes.forEach((n) => validateFormTaskAssignment(n, issues));

  // ── Check 8 — Info-level: nodes without labels (nudge for clarity) ──
  nodes.forEach((n) => {
    if (ENTRY_TYPES.has(n.type) || END_TYPES.has(n.type)) return;
    if (!n.label || !String(n.label).trim()) {
      issues.push({
        id: `no-label-${n.id}`,
        severity: 'info',
        code: 'NODE_WITHOUT_LABEL',
        nodeId: n.id,
        message: `${humanizeType(n.type)} node has no label. Labels appear in audit trails and execution overlays.`,
        suggest: 'Click the node title in the Inspector to rename.',
      });
    }
  });

  return { issues, stats: tallyStats(issues) };
}

// ──────────────────────────────────────────────────────────────────────
// Helpers (kept un-exported — internal to the validator)
// ──────────────────────────────────────────────────────────────────────

function emptyStats() {
  return { error: 0, warning: 0, info: 0, total: 0 };
}

function tallyStats(issues) {
  const s = emptyStats();
  issues.forEach((i) => {
    if (s[i.severity] != null) s[i.severity]++;
  });
  s.total = issues.length;
  return s;
}

/** BFS forward from a set of seed node ids over the outEdges adjacency. */
function bfs(seeds, outEdges) {
  const visited = new Set();
  const queue = [...seeds];
  while (queue.length > 0) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    (outEdges.get(id) || []).forEach((e) => {
      if (e.to && !visited.has(e.to)) queue.push(e.to);
    });
  }
  return visited;
}

/** BFS backward from a set of seed node ids over the inEdges adjacency. */
function reverseBfs(seeds, inEdges) {
  const visited = new Set();
  const queue = [...seeds];
  while (queue.length > 0) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    (inEdges.get(id) || []).forEach((e) => {
      if (e.from && !visited.has(e.from)) queue.push(e.from);
    });
  }
  return visited;
}

/**
 * Returns the set of node ids that participate in at least one cycle.
 * Uses Kahn's topological sort: any node NOT visited by Kahn is part
 * of a cycle (or downstream of one). For our purposes that's the right
 * shape — the panel just needs to know which nodes to highlight.
 */
function findCycleNodes(nodes, outEdges) {
  const indegree = new Map();
  nodes.forEach((n) => indegree.set(n.id, 0));
  nodes.forEach((n) => {
    (outEdges.get(n.id) || []).forEach((e) => {
      if (e.to != null) {
        indegree.set(e.to, (indegree.get(e.to) || 0) + 1);
      }
    });
  });
  const queue = nodes.filter((n) => (indegree.get(n.id) || 0) === 0).map((n) => n.id);
  const visited = new Set();
  while (queue.length > 0) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    (outEdges.get(id) || []).forEach((e) => {
      if (e.to == null) return;
      indegree.set(e.to, (indegree.get(e.to) || 0) - 1);
      if ((indegree.get(e.to) || 0) === 0) queue.push(e.to);
    });
  }
  const cycleNodes = new Set();
  nodes.forEach((n) => { if (!visited.has(n.id)) cycleNodes.add(n.id); });
  return cycleNodes;
}

/** A config value counts as "missing" when it's nullish, an empty string, or an empty array. */

/** Pretty-print a node type for human messages. e.g. 'http_task' → 'HTTP task'. */
function humanizeType(type) {
  if (!type) return 'Node';
  const known = {
    http_task: 'HTTP task',
    llm_task: 'LLM task',
    worker_task: 'Worker task',
    condition: 'Condition',
    switch_case: 'Switch',
    script_task: 'Script task',
    notification_task: 'Notification',
    parallel_fork: 'Parallel fork',
    fork: 'Fork',
    approval_gate: 'Approval gate',
    human_task: 'Human task',
    form_task: 'Form task',
    connector_task: 'Connector task',
    terminate: 'Terminate',
    end: 'End',
    manual_start: 'Start',
    start: 'Start',
  };
  return known[type] || type.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// ============================================================================
// RC-112b — DRIFT VALIDATOR (v4 mirror of v3Validate.js)
// ============================================================================
// See v3Validate.js for the full doc. Module-init assertion that every
// LEGACY_REQUIRED_CONFIG field name exists in the corresponding
// NODE_TYPES[type].configSchema. Throws in dev/test on drift.

(function validateRequiredConfigAgainstSchemas() {
  const errors = [];
  for (const [type, fields] of Object.entries(LEGACY_REQUIRED_CONFIG)) {
    const def = NODE_TYPES_FOR_DRIFT[type];
    if (!def) {
      errors.push(`LEGACY_REQUIRED_CONFIG['${type}']: no NODE_TYPES entry`);
      continue;
    }
    const declared = new Set(
      (def.configSchema || [])
        .map(schemaFieldName)
        .filter(Boolean),
    );
    let effectiveDeclared = declared;
    if (declared.size === 0 && def.aliasOf && NODE_TYPES_FOR_DRIFT[def.aliasOf]) {
      effectiveDeclared = new Set(
        (NODE_TYPES_FOR_DRIFT[def.aliasOf].configSchema || [])
          .map(schemaFieldName)
          .filter(Boolean),
      );
    }
    for (const fieldSpec of fields) {
      const alts = String(fieldSpec).split('|');
      const anyAlt = alts.some((alt) => effectiveDeclared.has(alt));
      if (!anyAlt) {
        errors.push(
          `LEGACY_REQUIRED_CONFIG['${type}']: declared field "${fieldSpec}" not in `
          + `NODE_TYPES['${type}'].configSchema [${[...effectiveDeclared].join(', ') || '(empty)'}]`,
        );
      }
    }
  }
  if (errors.length === 0) return;
  const msg = `[v4Validate/RC-112b] LEGACY_REQUIRED_CONFIG ↔ NODE_TYPES drift detected:\n  - ${errors.join('\n  - ')}\n`
    + `Fix in v4Validate.js: rename the field on the LHS to match the schema's field name, `
    + `OR add the missing field to soarNodes.js NODE_TYPES['<type>'].configSchema.`;
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    throw new Error(msg);
  } else if (typeof console !== 'undefined') {
    console.error(msg);
  }
})();
